import {
  CONTENT_IDEA_ORIGIN_LABELS,
  CONTENT_IDEA_TRANSITIONS,
  CONTENT_IDEA_TYPE_LABELS,
  CONTENT_INDUSTRY_LABELS,
  METRIC_SOLUTION_LABELS,
  type ContentIdea,
  type ContentIdeaOrigin,
  type ContentIdeaStatus,
  type ContentIdeaType,
  type ContentIndustry,
  type MetricSolution,
} from '@repo/types'

import { ROUTES } from 'routes'

import { ARTICLES_MANIFEST } from './articlesManifest.generated'

/**
 * VRG-740 — GTM → Articles → Ideas: labels, ordering and the status actions, pure so the panel and
 * the drawer read one definition. The status machine itself is `@repo/types`
 * CONTENT_IDEA_TRANSITIONS (the API enforces the same table).
 */

export const solutionLabel = (s: string) => METRIC_SOLUTION_LABELS[s as MetricSolution] ?? s
export const industryLabel = (s: string) => CONTENT_INDUSTRY_LABELS[s as ContentIndustry] ?? s
export const originLabel = (s: string) => CONTENT_IDEA_ORIGIN_LABELS[s as ContentIdeaOrigin] ?? s
export const typeLabel = (s: string) => CONTENT_IDEA_TYPE_LABELS[s as ContentIdeaType] ?? s

/** P1 → 0 … none → 3, so "sort by priority" puts unprioritised ideas last. */
export const priorityRank = (p: string | null | undefined): number =>
  p === 'P1' ? 0 : p === 'P2' ? 1 : p === 'P3' ? 2 : 3

/** Priority first, then score (higher first), then newest. The list's default order. */
export const compareIdeas = (a: ContentIdea, b: ContentIdea): number =>
  priorityRank(a.priority) - priorityRank(b.priority) ||
  (b.score ?? -1) - (a.score ?? -1) ||
  b.created_at.localeCompare(a.created_at)

/** Every write on Ideas and VERAfied guests needs this (and View GTM). Publishing an article does not. */
export const MANAGE_IDEAS_PERMISSION = 'internal_content_ideas_manage' as const
export const MANAGE_IDEAS_TIP = "You need the 'Manage ideas' permission. Ask an admin to grant it."

export interface StatusAction {
  to: ContentIdeaStatus
  label: string
  /** Rejecting asks for a reason; publishing asks for the article. */
  needs?: 'reason' | 'article'
}

/**
 * ONE approval, on the draft (Spencer, 2026-10-23). "Start brief" IS the selection of an idea;
 * there is no idea-level approve. `approved` is reachable only from `draft`.
 */
const FORWARD_LABEL: Partial<Record<ContentIdeaStatus, string>> = {
  brief: 'Start brief',
  draft: 'Start draft',
  approved: 'Approve draft',
  published: 'Mark published',
}

/** The moves offered from `status`, forward first, then back, then reject / reopen. */
export const statusActions = (status: ContentIdeaStatus): StatusAction[] => {
  const order = ['idea', 'brief', 'draft', 'approved', 'published'] as const
  const here = order.indexOf(status as (typeof order)[number])
  return CONTENT_IDEA_TRANSITIONS[status].map((to): StatusAction => {
    if (to === 'rejected') return { to, label: 'Reject', needs: 'reason' }
    if (status === 'rejected') return { to, label: 'Reopen' }
    const there = order.indexOf(to as (typeof order)[number])
    if (there > here)
      return {
        to,
        label: FORWARD_LABEL[to] ?? to,
        needs: to === 'published' ? 'article' : undefined,
      }
    return {
      to,
      label: status === 'published' ? 'Mark unpublished' : `Back to ${to}`,
    }
  })
}

/** The articles an idea can be marked published as: the website snapshot's, keyed section/slug. */
export const ARTICLE_OPTIONS = ARTICLES_MANIFEST.articles.map((a) => ({
  value: `${a.section}/${a.slug}`,
  label: a.title,
  rightLabel: a.path,
}))

export const articleFor = (idea: Pick<ContentIdea, 'published_section' | 'published_slug'>) =>
  idea.published_slug
    ? ARTICLES_MANIFEST.articles.find((a) => a.section === idea.published_section && a.slug === idea.published_slug) ??
      null
    : null

/** The SEO page's Recommendations section, where a fed idea's originating item lives. */
export const SEO_RECOMMENDATIONS_HREF = `${ROUTES.INTERNAL_GTM_SEO}?seo=recommendations`

export const isSeoFed = (origin: ContentIdeaOrigin) =>
  origin === 'seo_gap' || origin === 'rising_term' || origin === 'aeo_uncited'

/** VRG-741 — fed from the research corpus (a competitor's feed, a regulatory source, any source). */
export const isCorpusFed = (origin: ContentIdeaOrigin, ref: string | null) =>
  (origin === 'competitor_post' || origin === 'regulation' || origin === 'market_item') &&
  (ref ?? '').startsWith('market_item:')

/** The terms that carry an idea's score (the corpus feed records them). */
export const carriedBy = (basis: Record<string, unknown> | null): string[] =>
  Array.isArray(basis?.carriedBy) ? (basis!.carriedBy as string[]) : []

/** "Score 42.5" with the method one hover away; null when nothing SEO-derived backs it. */
export const scoreText = (score: number | null) =>
  score === null ? null : score.toLocaleString('en-US', { maximumFractionDigits: 1 })

export const scoreMethod = (basis: Record<string, unknown> | null): string | null =>
  typeof basis?.method === 'string' ? basis.method : null
